/**
 * Runtime — turn a generated Design JSON V2 document into a Design Descriptor.
 *
 * Design Memory compares designs by semantic fingerprint, and a fingerprint is
 * built from a descriptor. Until now nothing produced a descriptor from an
 * actual generated document, so nothing generated was ever remembered and the
 * uniqueness machinery had nothing to compare against.
 *
 * The descriptor reads the DOCUMENT for what the document decided (sections,
 * type scale, fill kinds, imagery, density) and the PLAN for what was intended
 * (concept, composition, hierarchy). Both matter: two documents built from one
 * plan can still differ, and that difference is exactly what memory must see.
 *
 * Pure and deterministic. No AI, no store, no I/O.
 */

import { allNodes, allSections } from '../artDirector';
import { VISUAL_DENSITY, NEGATIVE_SPACE } from '../memory';

const asArray = (v) => (Array.isArray(v) ? v : []);
const asObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
const choice = (plan, key) => asObject(asObject(plan)[key]).choice;
const uniqueSorted = (values) => [...new Set(values.filter(Boolean))].sort();

/** Node count → a coarse density band, so near-identical counts do not read as different. */
const densityBand = (nodeCount) => {
  if (nodeCount < 18) return VISUAL_DENSITY[0];   // low
  if (nodeCount < 36) return VISUAL_DENSITY[1];   // medium
  return VISUAL_DENSITY[2];                       // high
};

/** Sections per page → how much room the design gives itself. */
const negativeSpaceBand = (sectionCount, nodeCount) => {
  const perSection = sectionCount ? nodeCount / sectionCount : nodeCount;
  if (perSection > 8) return NEGATIVE_SPACE[0];   // tight
  if (perSection > 4) return NEGATIVE_SPACE[1];   // balanced
  return NEGATIVE_SPACE[2];                       // generous
};

/**
 * @param {Object} document  Design JSON V2 as returned by the model.
 * @param {Object} [context] { plan, genome, patterns }.
 * @returns {Object} a DesignDescriptor for createFingerprint.
 */
export const describeDocument = (document, context = {}) => {
  const doc = asObject(document);
  const plan = asObject(context.plan);
  const nodes = allNodes(doc);
  const sections = allSections(doc);
  const objects = nodes.filter((n) => n.node === 'object');

  const textNodes = objects.filter((n) => n.type === 'text');
  const fillKinds = uniqueSorted(nodes.flatMap((n) => asArray(n.fills).map((f) => asObject(f).kind)));
  const shapeKinds = uniqueSorted(objects.filter((n) => n.type === 'shape').map((n) => n.shape));
  const sectionRoles = asArray(sections).map((s) => asObject(s).role).filter(Boolean);
  const hasImagery = objects.some((n) => n.type === 'image') || nodes.some((n) => n.node === 'frame');
  const decorativeCount = nodes.filter((n) => n.layer === 'decoration').length;

  return {
    // Intent — what the plan decided.
    concept: choice(plan, 'creativeDirection') || asObject(doc.meta).title || null,
    composition: choice(plan, 'compositionStrategy') || null,
    hierarchy: asArray(choice(plan, 'hierarchyStrategy')),
    cta: choice(plan, 'ctaStrategy') || (sectionRoles.includes('cta') ? 'explicit' : 'none'),
    spacing: choice(plan, 'spacingStrategy') || null,
    rhythm: choice(plan, 'rhythmStrategy') || null,
    premiumCues: asArray(choice(plan, 'premiumCues')),

    // Execution — what the document actually did.
    sectionOrder: sectionRoles,
    heroPlacement: sectionRoles[0] || null,
    typography: uniqueSorted(textNodes.map((n) => n.style)).join('|') || null,
    color: fillKinds.join('|') || null,
    shape: shapeKinds.join('|') || null,
    decoration: decorativeCount > 0 ? `decorated:${Math.min(decorativeCount, 6)}` : 'none',
    photography: hasImagery ? 'photographic' : 'none',
    illustration: objects.some((n) => n.type === 'vector') ? 'vector' : 'none',
    componentUsage: uniqueSorted(nodes.filter((n) => n.node === 'component').map((n) => n.component || n.role)),
    objectRelationships: uniqueSorted(nodes.filter((n) => n.mask).map((n) => `mask:${asObject(n.mask).kind || 'shape'}`)),
    visualDensity: densityBand(nodes.length),
    negativeSpace: negativeSpaceBand(sections.length, nodes.length),
    balance: choice(plan, 'balanceStrategy') || null,

    // Provenance.
    genome: asObject(context.genome).id || asObject(doc.meta).genome || null,
    libraryPatterns: asArray(context.patterns).map((p) => asObject(p).id).filter(Boolean),
  };
};
